import constantsTextNodes from '@data/i18n/constants.json'

import { ORDER_FULFILLMENT_STATUS, lang } from '@lib/constants'
import { getOrderStatus, getShipmentProgress } from '@lib/operations/customer'
import type { Order } from '@lib/types/order'
import type { SubscriptionBoxHistoryNode } from '@lib/types/subscription-box'
import { extractMonthYear, formatDateString } from '@lib/utils/dates'

import ShipmentTracker from '@components/ShipmentTracker'
import Price from '@components/product/ProductView/Price'

type Props = {
	order: Order | SubscriptionBoxHistoryNode
	inModal?: boolean
}

export default function OrderDetails({ order, inModal = false }: Props) {
	// Convert createdAt date to Month only
	const dateString = (order as SubscriptionBoxHistoryNode).createdAt
	const createdAtMonth = extractMonthYear(dateString)

	const formattedDate =
		'createdAt' in order ? createdAtMonth : formatDateString((order as Order).processedAt)

	// Subscription orders status
	const orderStatus =
		'month' in order
			? {
					status: (order as SubscriptionBoxHistoryNode).shippingDetail?.fulfillmentStatus,
					message:
						ORDER_FULFILLMENT_STATUS[
							(order as SubscriptionBoxHistoryNode).shippingDetail?.fulfillmentStatus
						]
			  }
			: getOrderStatus(order as Order)
	const shipmentProgress = getShipmentProgress(orderStatus)

	const orderNumber =
		(order as Order)?.orderNumber ||
		(order as SubscriptionBoxHistoryNode).shippingDetail?.orderNumber

	return (
		<div className={`flex-1 flex flex-col ${inModal ? '' : 'p-6 pb-12'}`}>
			<div className={`flex justify-between pb-4 ${inModal ? 'pt-4 px-12' : ''}`}>
				{/* Month */}
				{formattedDate && <h3 className="font-medium">{formattedDate}</h3>}

				{/* Order price */}
				{(order as Order)?.currentTotalPrice?.amount && (
					<span className="ml-auto font-medium text-gray-500">
						<Price
							price={{
								amount: (order as Order).currentTotalPrice.amount,
								currencyCode:
									lang === 'es-es' ? 'EUR' : (order as Order).currentTotalPrice.currencyCode
							}}
						/>
					</span>
				)}
			</div>

			<div
				className={`flex flex-col pt-4 border-t border-gray-200 gap-y-3 ${inModal ? 'px-12' : ''}`}
			>
				{/* Order number */}
				{orderNumber && (
					<div className="flex justify-between text-sm font-light">
						<span>{lang === 'es-es' ? 'Número de orden' : 'Order Number'}</span>
						<span className="text-gray-500">{orderNumber}</span>
					</div>
				)}

				{/* Order status */}
				{orderStatus?.message && (
					<div className="flex justify-between text-sm font-light">
						<span>{lang === 'es-es' ? 'Estado del pedido' : 'Order Status'}</span>
						<span className="text-gray-500">{constantsTextNodes[orderStatus.message]}</span>
					</div>
				)}
			</div>

			{/* Shipment tracker */}
			<div className={`mx-4 mt-10 ${inModal ? 'mb-8 px-12' : ''}`}>
				<ShipmentTracker shipmentProgress={shipmentProgress} />
			</div>
		</div>
	)
}
